import React, { useState, useEffect } from 'react';
import { Card, Table, Row, Col, Statistic, Tag, Typography, Space, DatePicker, Select, Input, Button } from 'antd';
import { CheckCircleOutlined, DollarOutlined, TeamOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const adminApi = axios.create({ baseURL: '/api', timeout: 30000 });
adminApi.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const formatCurrency = (v) =>
  'Rp ' + (parseFloat(v) || 0).toLocaleString('id-ID');

const Settlements = () => {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({ total: 0, amount: 0, fee: 0, net: 0 });
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [merchantFilter, setMerchantFilter] = useState('');
  const [merchants, setMerchants] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  useEffect(() => {
    fetchMerchants();
    fetchData();
  }, []);

  const fetchMerchants = async () => {
    try {
      const res = await adminApi.get('/users?role=merchant&limit=100');
      setMerchants(res.data?.users || res.data?.data || []);
    } catch { /* silent */ }
  };

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pagination.pageSize,
        transaction_type: 'cash_in',
        status: 'success',
      };
      if (dateRange.length === 2) {
        params.date_from = dateRange[0].format('YYYY-MM-DD');
        params.date_to   = dateRange[1].format('YYYY-MM-DD');
      }
      if (merchantFilter) params.merchant_id = merchantFilter;

      const res = await adminApi.get('/transactions', { params });
      const rows = res.data?.transactions || [];

      setData(rows);
      setPagination(prev => ({ ...prev, current: page, total: res.data?.pagination?.total || rows.length }));

      // Hitung summary dari baris yang dimuat
      const tot = rows.reduce((acc, r) => ({
        amount: acc.amount + (parseFloat(r.amount) || 0),
        fee:    acc.fee    + (parseFloat(r.fee)    || 0),
        net:    acc.net    + (parseFloat(r.total_amount) || 0) - (parseFloat(r.fee) || 0) - (parseFloat(r.fee_system) || 0),
      }), { amount: 0, fee: 0, net: 0 });

      setSummary({ total: rows.length, ...tot });
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => { fetchData(1); };
  const handleReset  = () => { setDateRange([]); setMerchantFilter(''); fetchData(1); };

  const columns = [
    {
      title: 'Tanggal',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: v => dayjs(v).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Transaction ID',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 220,
      render: v => <Text code style={{ fontSize: 11 }}>{v}</Text>,
    },
    {
      title: 'Merchant',
      dataIndex: 'merchant_name',
      key: 'merchant_name',
      render: (v, r) => v || r.customer_name || '-',
    },
    {
      title: 'Jumlah',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: v => formatCurrency(v),
    },
    {
      title: 'Fee',
      dataIndex: 'fee',
      key: 'fee',
      align: 'right',
      render: v => <Text type="secondary">{formatCurrency(v)}</Text>,
    },
    {
      title: 'Net Diterima',
      key: 'net',
      align: 'right',
      render: (_, r) => {
        const net = (parseFloat(r.total_amount) || 0) - (parseFloat(r.fee) || 0) - (parseFloat(r.fee_system) || 0);
        return <Text strong style={{ color: '#52c41a' }}>{formatCurrency(net)}</Text>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: () => <Tag color="green" icon={<CheckCircleOutlined />}>Settled</Tag>,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3}>Settlement</Title>
        <Text type="secondary">Transaksi cash-in yang sudah masuk ke saldo available merchant (delay = 0)</Text>
      </div>

      {/* Summary */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="Transaksi Halaman Ini" value={summary.total} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Total Gross" value={formatCurrency(summary.amount)} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Total Fee" value={formatCurrency(summary.fee)} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Total Net Merchant" value={formatCurrency(summary.net)} prefix={<DollarOutlined />} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      {/* Filter */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={v => setDateRange(v || [])}
            format="DD/MM/YYYY"
            placeholder={['Dari tanggal', 'Sampai tanggal']}
          />
          <Select
            placeholder="Semua Merchant"
            allowClear
            style={{ width: 220 }}
            value={merchantFilter || undefined}
            onChange={v => setMerchantFilter(v || '')}
            showSearch
            optionFilterProp="children"
          >
            {merchants.map(m => (
              <Option key={m.id} value={m.id}>{m.full_name || m.username}</Option>
            ))}
          </Select>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>Cari</Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>Reset</Button>
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="transaction_id"
          loading={loading}
          pagination={{
            ...pagination,
            showTotal: t => `Total ${t} transaksi`,
            onChange: page => fetchData(page),
          }}
          scroll={{ x: 900 }}
        />
      </Card>
    </div>
  );
};

export default Settlements;
